import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardBody, Col, Container, Input, Label, Row } from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import FormModal from '../components/FormModal';
import LoadingSpiner from '../components/LoadingSpiner';
import { capitalizeWords, formatPrice } from '../components/capitalizeFunction';
import { deleteButton } from '../components/AlerteModal';
import {
  useDeleteDepense,
  usePagignationDepenses,
} from '../../Api/queriesDepense';
import DepenseForm from './DepenseForm';
import { connectedUserBoutique } from '../Authentication/userInfos';
import ListPagination from '../components/ListPagination';

export default function DepenseListe() {
  const [page, setPage] = useState(1);
  const limit = 30;
  const [form_modal, setForm_modal] = useState(false);
  const [formModalTitle, setFormModalTitle] = useState('Ajouter une Dépense');
  const { mutate: deleteDepense, isDeleting } = useDeleteDepense();
  const [depenseToUpdate, setDepenseToUpdate] = useState(null);
  const [todayExpense, setTodayExpense] = useState(false);
  const [selectedBoutique, setSelectedBoutique] = useState(null);

  // Search State
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * -----------------------------
   * MODIF (recherche pro + pagination cohérente + cache)
   * -----------------------------
   * Avant: filtrage côté frontend (search/boutique/today) sur la page courante.
   * Maintenant:
   * - debounce 300ms sur searchTerm
   * - on passe search/boutique/today au backend (filtre AVANT pagination)
   * - le cache React Query est isolé par queryKey (corrigé dans le hook)
   */
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, selectedBoutique, todayExpense]);

  const {
    data: depenseData,
    isLoading,
    error,
  } = usePagignationDepenses(
    page,
    limit,
    debouncedSearchTerm,
    selectedBoutique,
    todayExpense
  );

  const filterSearchDepense = useMemo(
    () => depenseData?.results?.data || [],
    [depenseData]
  );

  // Total Expense
  const sumTotalExpense = filterSearchDepense?.reduce(
    (curr, item) => (curr += item?.totalAmount),
    0
  );

  const totalDepensesPage = filterSearchDepense?.length ?? 0;

  const hasActiveFilters =
    Boolean(searchTerm) || todayExpense || selectedBoutique != null;

  const resetFilters = () => {
    setSearchTerm('');
    setTodayExpense(false);
    setSelectedBoutique(null);
  };

  // Ouverture de Modal Form
  function tog_form_modal() {
    setForm_modal(!form_modal);
  }

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <Breadcrumbs title='Transaction' breadcrumbItem='Depense' />

          {/* -------------------------- */}
          <FormModal
            form_modal={form_modal}
            setForm_modal={setForm_modal}
            tog_form_modal={tog_form_modal}
            modal_title={formModalTitle}
            size='md'
            bodyContent={
              <DepenseForm
                depenseToEdit={depenseToUpdate}
                tog_form_modal={tog_form_modal}
              />
            }
          />

          {/* -------------------- */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id='depenseList'>
                    {/* Barre outils + filtres + synthèse */}
                    <div className='border rounded-3 p-3 mb-3 bg-light'>
                      <Row className='g-3 align-items-end'>
                        <Col xl={3} lg={4} md={6}>
                          <Button
                            color='info'
                            className='add-btn w-100'
                            id='create-btn'
                            onClick={() => {
                              setDepenseToUpdate(null);
                              tog_form_modal();
                            }}
                          >
                            <i className='fas fa-dollar-sign align-center me-1'></i>
                            Ajouter une Dépense
                          </Button>
                        </Col>

                        <Col xl={3} lg={4} md={6}>
                          <Label
                            htmlFor='filterBoutiqueDepense'
                            className='fw-semibold mb-1'
                          >
                            Boutique
                          </Label>
                          <div className='d-flex gap-2'>
                            <Input
                              id='filterBoutiqueDepense'
                              type='select'
                              bsSize='sm'
                              value={selectedBoutique ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setSelectedBoutique(
                                  v === '' ? null : Number(v)
                                );
                              }}
                              className='border border-dark flex-grow-1'
                              style={{ cursor: 'pointer' }}
                            >
                              <option value=''>Toutes les boutiques</option>
                              <option value={connectedUserBoutique ?? 0}>
                                {connectedUserBoutique ?? 0} — Ma boutique
                              </option>
                              <optgroup label='Raccourcis'>
                                <option value='1'>Boutique 1</option>
                                <option value='2'>Boutique 2</option>
                              </optgroup>
                            </Input>
                            <Input
                              type='number'
                              bsSize='sm'
                              className='border border-dark'
                              placeholder='N°'
                              title='Numéro de boutique'
                              style={{ maxWidth: '90px' }}
                              value={selectedBoutique ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setSelectedBoutique(
                                  raw === '' ? null : Number(raw)
                                );
                              }}
                            />
                          </div>
                        </Col>

                        <Col xl={4} lg={4} md={8}>
                          <Label
                            htmlFor='filterSearchDepense'
                            className='fw-semibold mb-1'
                          >
                            Recherche
                          </Label>
                          <div className='d-flex gap-2'>
                            <Input
                              id='filterSearchDepense'
                              type='text'
                              bsSize='sm'
                              className='border border-dark'
                              placeholder='Motif, montant…'
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm !== '' && (
                              <Button
                                color='danger'
                                size='sm'
                                outline
                                title='Effacer la recherche'
                                onClick={() => setSearchTerm('')}
                              >
                                <i className='fas fa-times'></i>
                              </Button>
                            )}
                          </div>
                        </Col>

                        <Col xl={2} lg={12} className='text-lg-end'>
                          {hasActiveFilters && (
                            <Button
                              color='secondary'
                              size='sm'
                              outline
                              className='w-100 w-md-auto'
                              onClick={resetFilters}
                            >
                              <i className='ri-filter-off-line me-1'></i>
                              Réinitialiser
                            </Button>
                          )}
                        </Col>
                      </Row>

                      <Row className='g-3 mt-1 align-items-stretch'>
                        <Col lg={5}>
                          <Label className='fw-semibold mb-2 d-block'>
                            Filtres rapides
                          </Label>
                          <Button
                            color={todayExpense ? 'warning' : 'light'}
                            size='sm'
                            className={
                              todayExpense
                                ? 'text-white'
                                : 'border border-secondary text-dark'
                            }
                            onClick={() => setTodayExpense(!todayExpense)}
                          >
                            <i className='ri-calendar-line me-1'></i>
                            Dépenses d&apos;aujourd&apos;hui
                          </Button>
                        </Col>

                        {!isLoading && !error && (
                          <Col lg={7}>
                            <Label className='fw-semibold mb-2 d-block'>
                              Synthèse (page courante)
                            </Label>
                            <Row className='g-2'>
                              <Col xs={6}>
                                <div className='border rounded bg-white p-2 text-center h-100'>
                                  <div className='text-muted small'>
                                    Nombre de lignes
                                  </div>
                                  <div className='text-primary fw-bold'>
                                    {totalDepensesPage}
                                  </div>
                                </div>
                              </Col>
                              <Col xs={6}>
                                <div className='border rounded bg-white p-2 text-center h-100'>
                                  <div className='text-muted small'>
                                    Total dépensé
                                  </div>
                                  <div className='text-danger fw-bold'>
                                    {formatPrice(sumTotalExpense)} F
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        )}
                      </Row>
                    </div>

                    <ListPagination
                      page={depenseData?.results?.page ?? page}
                      totalPages={depenseData?.results?.totalPages ?? 1}
                      onPageChange={setPage}
                      nextDisabled={page === depenseData?.results?.totalPages}
                      className='mt-2 mb-2'
                    />
                    {error && (
                      <div className='text-danger text-center'>
                        Erreur de chargement des données
                      </div>
                    )}
                    {isLoading && <LoadingSpiner />}

                    <div className='table-responsive table-card mt-3 mb-1'>
                      {filterSearchDepense?.length === 0 && (
                        <div className='text-center text-mutate'>
                          Aucune Dépense trouvée !
                        </div>
                      )}
                      {!error &&
                        !isLoading &&
                        filterSearchDepense.length > 0 && (
                          <table
                            className='table align-middle table-nowrap'
                            id='depenseTable'
                          >
                            <thead className='table-light'>
                              <tr className='text-center align-middle'>
                                <th style={{ minWidth: '110px' }}>Date</th>
                                <th>Motif</th>
                                <th style={{ minWidth: '120px' }}>
                                  Montant
                                </th>
                                <th style={{ width: '120px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody className='list form-check-all'>
                              {filterSearchDepense?.length > 0 &&
                                filterSearchDepense?.map((depense) => (
                                  <tr key={depense._id} className='text-center'>
                                    <td>
                                      {new Date(
                                        depense.dateOfDepense
                                      ).toLocaleDateString()}{' '}
                                    </td>

                                    <td className='text-wrap'>
                                      {capitalizeWords(depense.motifDepense)}
                                    </td>

                                    <td className='text-danger'>
                                      {formatPrice(depense.totalAmount)}
                                      {' F '}
                                    </td>

                                    {connectedUserBoutique ===
                                      depense?.user?.boutique && (
                                      <td>
                                        <div className='d-flex gap-2'>
                                          <div className='edit'>
                                            <button
                                              className='btn btn-sm btn-success edit-item-btn'
                                              onClick={() => {
                                                setFormModalTitle(
                                                  'Modifier les données'
                                                );
                                                setDepenseToUpdate(depense);
                                                tog_form_modal();
                                              }}
                                            >
                                              <i className='ri-pencil-fill text-white'></i>
                                            </button>
                                          </div>
                                          {isDeleting && <LoadingSpiner />}
                                          {!isDeleting && (
                                            <div className='remove'>
                                              <button
                                                className='btn btn-sm btn-danger remove-item-btn'
                                                data-bs-toggle='modal'
                                                data-bs-target='#deleteRecordModal'
                                                onClick={() => {
                                                  deleteButton(
                                                    depense._id,
                                                    `depense de ${depense.totalAmount} F
                                                   `,
                                                    deleteDepense
                                                  );
                                                }}
                                              >
                                                <i className='ri-delete-bin-fill text-white'></i>
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
